import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';

import { API_CONFIG } from '../config/api.config';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface AuthUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  /**
   * Role names exactly as they travel on the wire from POST /auth/login,
   * /auth/google and /auth/register — no "ROLE_" prefix (the backend adds that
   * only when it builds Spring Security authorities). Verified against a real
   * login payload: e.g. ["ADMIN"]. Match against these with `hasRole()`, not
   * substring checks. Older persisted sessions may not have this field, so
   * every reader must treat it as possibly undefined.
   */
  roles?: string[];
}

export interface AuthResponse {
  accessToken: string;
  tokenType: string;
  expiresInMs: number;
  user: AuthUser;
}

const AUTH_STORAGE_KEY = 'hirefit-auth';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly authState = signal<AuthResponse | null>(this.readStoredAuth());

  readonly auth = this.authState.asReadonly();

  login(request: LoginRequest): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${API_CONFIG.baseUrl}/auth/login`, request)
      .pipe(tap((response) => this.setAuth(response)));
  }

  /**
   * Exchanges a Google Identity Services ID token for a HireFit session.
   * The backend resolves sign-up vs. sign-in on its side, so there is a single
   * frontend flow for both /login and /register.
   */
  loginWithGoogle(idToken: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${API_CONFIG.baseUrl}/auth/google`, { idToken })
      .pipe(tap((response) => this.setAuth(response)));
  }

  register(request: RegisterRequest): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${API_CONFIG.baseUrl}/auth/register`, request)
      .pipe(tap((response) => this.setAuth(response)));
  }

  logout(): void {
    this.clearSession();
    void this.router.navigateByUrl('/');
  }

  /**
   * Clears the session and sends the user to /login.
   * Used by the auth interceptor on 401, and by profile email-change (JWT no longer matches).
   */
  handleUnauthorized(options?: { queryParams?: Record<string, string> }): void {
    this.clearSession();
    void this.router.navigate(['/login'], {
      queryParams: options?.queryParams,
    });
  }

  /** Clears auth state and localStorage without navigating. */
  clearSession(): void {
    this.authState.set(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }

  private setAuth(response: AuthResponse): void {
    this.authState.set(response);
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(response));
  }

  private readStoredAuth(): AuthResponse | null {
    try {
      const rawValue = localStorage.getItem(AUTH_STORAGE_KEY);
      return rawValue ? (JSON.parse(rawValue) as AuthResponse) : null;
    } catch {
      return null;
    }
  }
}
